import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Sparkles, MessageSquare, Repeat, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ICON_MAP = {
  match: Sparkles,
  offer: Repeat,
  accept: Check,
  reject: X,
  message: MessageSquare,
};

function relativeTime(iso) {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
    } catch (e) { console.warn("Notifications load failed", e); setItems([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      toast.success("All marked read");
      load();
    } catch (e) { console.warn("Notifications mark-all failed", e); }
  };

  return (
    <div className="section-pad py-8 md:py-12 max-w-2xl mx-auto" data-testid="notifications-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display font-bold text-3xl tracking-tight">Notifications</h1>
        {items.some(n => !n.read) && (
          <button onClick={markAll} className="btn-pill btn-soft !text-sm !py-2" data-testid="mark-all-read">Mark all read</button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="skeleton h-16" />)}</div>
      ) : items.length === 0 ? (
        <div className="vyrti-card p-10 text-center" data-testid="notifications-empty">
          <div className="w-14 h-14 rounded-full bg-[#F1F5F9] mx-auto flex items-center justify-center mb-3">
            <Bell className="w-6 h-6 text-zinc-400" />
          </div>
          <p className="text-zinc-600">No notifications yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(n => {
            const Icon = ICON_MAP[n.type] || Bell;
            const Wrap = n.link ? Link : "div";
            const props = n.link ? { to: n.link } : {};
            return (
              <Wrap key={n.id} {...props} className={`vyrti-card !shadow-none p-4 flex items-start gap-3 border ${n.read ? "border-[#E2E2E2]" : "border-[#0654BA]/30 bg-[#F8FAFF]"}`} data-testid={`notification-${n.id}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${n.read ? "bg-[#F1F5F9] text-zinc-500" : "bg-[#0654BA] text-white"}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{n.title}</p>
                  <p className="text-sm text-zinc-600 mt-0.5">{n.body}</p>
                  <p className="text-[11px] text-zinc-400 mt-1">{relativeTime(n.created_at)}</p>
                </div>
              </Wrap>
            );
          })}
        </div>
      )}
    </div>
  );
}
